<script lang="ts" setup>
import { useMate } from "@/stores/mate";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import i18n from "@/locales";
import { copyToClipboard } from "@/modules/common";
import { useAdmire } from "@/stores/admire";
import { useReply } from "@/stores/reply";
import { MediaUploadFileItem } from "@/types/component";
import { Mate } from "@/types/entity";
import { goLoginIfNot } from "@/use/go";
import { useGlobalUser } from "@/stores/globalUser";
import { useName } from "@/use/common";
// 常量
// 状态
const props = defineProps<{
  mate: Mate;
}>();

const route = useRoute();
const router = useRouter();
const mateStore = useMate();
const ocList = ref<Array<Mate>>([]);
const currentMate = ref<Mate | null>(null);

const isMyUser = ref(false);

const activeTab = ref(0);

const admireStore = useAdmire();

const admireRecord = ref<Admirer | null>(null);
const isAdmireBtnLoading = ref(false);

const clickAdmireBtn = async () => {
  if (!(await goLoginIfNot())) return;
  try {
    isAdmireBtnLoading.value = true;
    if (!admireRecord.value) {
      admireRecord.value = await admireStore.admire(currentMate.value!.id!);
    }
  } catch (e) {
    showFailToast(i18n.global.t("Message.operationFailed"));
    throw e;
  } finally {
    isAdmireBtnLoading.value = false;
  }
};

const replyStore = useReply();
const reply = computed(() => currentMate.value!.reply!);

const showAvatarUploadPopup = ref(false);

const onAvatarSelected = async ([file]: MediaUploadFileItem[]) => {
  const { mediaId, url } = file;
  currentMate.value!.mediaId = mediaId;
  currentMate.value!.mediaUrl = url!;
  onSave({ mediaId, mediaUrl: url! });
};

const showNameInputModal = ref(false);
const onNameInputConfirm = (text: string) => {
  currentMate.value!.name = text;
  showNameInputModal.value = false;
  onSave({ name: text });
};

const onSave = async (params: Partial<Mate>) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.saving"),
    duration: 0,
  });
  try {
    await mateStore.updateMateDetail(currentMate.value!.uuid, params);
  } finally {
    loading.close();

    showSuccessToast(i18n.global.t("Message.saveSuccess"));
  }
};

const onShare = () => {
  copyToClipboard(window.location.href);
  showSuccessToast(i18n.global.t("Message.copySuccessful"));
};

const isLoading = ref(false);
onMounted(async () => {
  const uuid = route.params.uuid as string;
  if (!uuid) return;

  isLoading.value = true;
  const mateList = await mateStore.fetchOCListByUserMateId(uuid);
  let totalLike = 0;
  mateList.forEach((item) => {
    if (item.status === 0) return;

    totalLike += item.emojiCount;
    if (item.isUser) {
      currentMate.value = item;
    } else {
      ocList.value.push(item);
    }
  });

  if (!currentMate.value) {
    currentMate.value = props.mate;
  }

  currentMate.value!.emojiCount = totalLike;

  // 查询好友状态
  if (!useGlobalUser().isLogin) {
    isLoading.value = false;
    return;
  }

  const isMyMate = mateStore.isMyMate(uuid);
  let p1: Promise<void> = Promise.resolve();
  if (!isMyMate) {
    p1 = admireStore.isAdmiring(currentMate.value!.id!).then((record) => {
      admireRecord.value = record ?? null;
    });
  }
  const p2 = replyStore.getOrFetchReplyList(currentMate.value!.replyId!);
  Promise.all([p1, p2]).finally(() => {
    isLoading.value = false;
  });

  isMyUser.value = uuid === mateStore.userMate.uuid;
});
</script>
<template>
  <div class="h-full overflow-hidden grid grid-rows-[auto_1fr]">
    <ThreadsNav :show-center="false" :show-back="true">
      <template #right>
        <div class="flex items-center gap-2">
          <!-- <Share /> -->
          <button class="btn btn-sm btn-outline" @click="onShare">
            <i class="iconify mdi--share-outline"></i>
            <span>{{ $t("User.shareAuthor") }}</span>
          </button>
        </div>
      </template>
    </ThreadsNav>

    <div v-if="isLoading" class="flex items-center justify-center">
      <span class="loading loading-ring"></span>
    </div>
    <!-- 用户信息区域 -->
    <div v-else class="h-full overflow-y-auto bg-base-200">
      <div class="px-4 pt-4" v-if="currentMate">
        <div class="flex flex-col items-center">
          <div class="w-[90px] mb-2 relative">
            <UserAvatar :mate="currentMate" class="w-full" />
            <div
              v-if="isMyUser"
              class="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-secondary text-secondary-content flex items-center justify-center"
              @click="showAvatarUploadPopup = true"
            >
              <i class="iconify mdi--edit text-[0.6rem]"></i>
            </div>
          </div>
          <div class="text-lg font-bold flex items-center gap-2">
            {{ currentMate?.name }}
            <div
              v-if="isMyUser"
              class="text-sm text-base-content/50"
              @click="showNameInputModal = true"
            >
              <i class="iconify mdi--edit w-4 transform translate-y-[2px]"></i>
            </div>
          </div>
          <button
            v-if="!isMyUser"
            :class="[
              'btn btn-sm px-5 mt-2',
              admireRecord ? 'btn-outline' : 'btn-primary',
            ]"
            :disabled="isLoading || isAdmireBtnLoading || !!admireRecord"
            @click="clickAdmireBtn"
          >
            <span
              v-if="isLoading || isAdmireBtnLoading"
              class="loading loading-xs"
            />
            <template v-if="!admireRecord || admireRecord.status === 3">
              <i class="iconify mingcute--user-add-2-line"></i>
              {{ $t("User.mate.addFriend") }}
            </template>
            <template v-else-if="!admireRecord.status">
              <i class="iconify mingcute--check-circle-line"></i>
              {{ $t("User.mate.hasBeenFriend") }}
            </template>
            <template v-else-if="admireRecord.status === 1">
              <i class="iconify mingcute--sandglass-line"></i>
              {{ $t("User.mate.friendRequested") }}
            </template>
          </button>

          <div class="flex items-center gap-1 mb-3 mt-2">
            <span>
              {{ $t("User.home.like") }}: {{ currentMate?.emojiCount }}
            </span>
          </div>

          <!-- 统计信息 -->
          <div class="flex items-center gap-6 text-sm" v-if="!isMyUser">
            <ReplyEmoji :reply="reply!" />
          </div>
        </div>
      </div>

      <!-- 暂时不是好友的情况 -->
      <!-- <div
        v-if="!isMyUser && (!admireRecord || admireRecord.status !== undefined)"
        class="flex items-center justify-center"
      >
        <VanEmpty :description="$t('Message.notFriend')" />
      </div> -->

      <!-- 标签页 -->
      <VanTabs v-model:active="activeTab" sticky offset-top="66">
        <VanTab
          :title="
            $t(isMyUser ? 'User.home.myOc' : 'User.home.oc', {
              count: ocList.length,
            })
          "
          name="oc"
        >
          <div v-if="ocList.length === 0" class="w-full pt-4">
            <VanEmpty :description="$t('User.home.noOc')" />
          </div>
          <div v-else class="grid grid-cols-4 gap-2 p-4 lg:grid-cols-8">
            <template v-for="(item, index) in ocList">
              <div
                v-if="item.status !== 0"
                :key="index"
                class="aspect-[3/4]"
                @click="
                  router.push({ name: 'mateHome', params: { uuid: item.uuid } })
                "
              >
                <UserAvatar :mate="item" class="w-full h-full rounded-lg" />
                <div class="mt-1 text-sm text-center">
                  <div
                    class="line-clamp-1"
                    :class="{
                      'text-base-content/50': !item.name,
                    }"
                  >
                    {{ useName(item.name) }}
                  </div>
                  <!-- <div class="text-base-content/50 text-xs">{{ item.bio }}</div> -->
                </div>
              </div>
            </template>
          </div>
        </VanTab>
        <VanTab :title="$t('Community.moment')" name="moment">
          <moment-module :mate="currentMate!" :show-create="false" />
        </VanTab>

        <VanTab :title="i18n.global.t('collection.window')" name="collection">
          <div class="pb-2">
            <CollectionList
              :mate-id="currentMate!.id"
              edit
              :show-create="false"
            />
          </div>
        </VanTab>

        <!-- <VanTab :title="i18n.global.t('Community.story')" name="story">
          <mate-story-book-list :mate-id="currentMate!.id" hide-tab />
        </VanTab>
        <VanTab :title="i18n.global.t('Community.game')" name="game">
          <GalGameModule :mate-id="currentMate!.id" :show-create="false" />
        </VanTab> -->
      </VanTabs>

      <MediaSelectPopup
        v-model:show="showAvatarUploadPopup"
        :allow-crop-outside="false"
        :enable-crop="true"
        :crop-aspect-ratio="1 / 1"
        :max-count="1"
        :multiple="false"
        :upload-type="0"
        :enable-mate-gallery="false"
        :enable-use-avatar="false"
        media-type="img"
        replace
        @on-media-selected="onAvatarSelected"
      />
      <BaseInputModal
        :title="i18n.global.t('User.fillName')"
        v-model:show="showNameInputModal"
        @confirm="onNameInputConfirm"
      />
    </div>
  </div>
</template>
<style>
.gallery-uploader .van-uploader__input-wrapper {
  width: 100%;
}

.gallery-uploader .van-uploader__wrapper {
  @apply flex gap-0;
}
</style>
